// Comprehensive validation utilities for the application
// All validation functions are safe and return clear error messages

/**
 * Email validation with comprehensive checks
 * @param {string} email - Email to validate
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim();
  
  if (trimmedEmail.length === 0) {
    return { isValid: false, error: 'Email is required' };
  }

  if (trimmedEmail.length > 254) {
    return { isValid: false, error: 'Email is too long (maximum 254 characters)' };
  }

  // Comprehensive email regex pattern
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  return { isValid: true, error: '' };
};

/**
 * Password validation with security requirements
 * @param {string} password - Password to validate
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    return { isValid: false, error: 'Password is required' };
  }

  if (password.length < 6) {
    return { isValid: false, error: 'Password must be at least 6 characters long' };
  }

  if (password.length > 128) {
    return { isValid: false, error: 'Password is too long (maximum 128 characters)' };
  }

  // Check for common weak passwords
  const weakPasswords = ['password', '123456', 'qwerty', 'admin', 'user'];
  if (weakPasswords.includes(password.toLowerCase())) {
    return { isValid: false, error: 'Please choose a stronger password' };
  }

  // Check for at least one letter and one number (complexity requirement)
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /\d/.test(password);

  if (!hasLetter || !hasNumber) {
    return { isValid: false, error: 'Password must contain at least one letter and one number' };
  }

  return { isValid: true, error: '' };
};

/**
 * Password confirmation validation
 * @param {string} password - Original password
 * @param {string} confirmPassword - Confirmation password
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validatePasswordConfirmation = (password, confirmPassword) => {
  if (!confirmPassword || typeof confirmPassword !== 'string') {
    return { isValid: false, error: 'Please confirm your password' };
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: 'Passwords do not match' };
  }

  return { isValid: true, error: '' };
};

/**
 * Name validation (for full names, customer names, etc.)
 * @param {string} name - Name to validate
 * @param {string} fieldName - Name of the field for error messages
 * @param {object} options - Validation options
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateName = (name, fieldName = 'Name', options = {}) => {
  const {
    allowAlphanumeric = false, // Allow numbers and special chars for customer names
    allowSpecialChars = false  // Allow special characters
  } = options;

  if (!name || typeof name !== 'string') {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const trimmedName = name.trim();
  
  if (trimmedName.length === 0) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (trimmedName.length < 2) {
    return { isValid: false, error: `${fieldName} must be at least 2 characters long` };
  }

  if (trimmedName.length > 100) {
    return { isValid: false, error: `${fieldName} is too long (maximum 100 characters)` };
  }

  // Different validation patterns based on options
  let nameRegex;
  if (allowAlphanumeric) {
    // Allow letters, numbers, spaces, hyphens, apostrophes, periods, and common business characters
    nameRegex = /^[a-zA-Z0-9\s\-'.&()]+$/;
  } else if (allowSpecialChars) {
    // Allow letters, spaces, hyphens, apostrophes, and periods
    nameRegex = /^[a-zA-Z\s\-'.]+$/;
  } else {
    // Standard name validation (letters, spaces, hyphens, apostrophes)
    nameRegex = /^[a-zA-Z\s\-']+$/;
  }

  if (!nameRegex.test(trimmedName)) {
    if (allowAlphanumeric) {
      return { isValid: false, error: `${fieldName} contains invalid characters. Only letters, numbers, spaces, hyphens, apostrophes, periods, and ampersands are allowed.` };
    } else {
      return { isValid: false, error: `${fieldName} contains invalid characters` };
    }
  }

  return { isValid: true, error: '' };
};

/**
 * Organization code validation
 * @param {string} code - Organization code to validate
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateOrganizationCode = (code) => {
  if (!code || typeof code !== 'string') {
    return { isValid: false, error: 'Organization code is required' };
  }

  const trimmedCode = code.trim().toUpperCase();
  
  if (trimmedCode.length === 0) {
    return { isValid: false, error: 'Organization code is required' };
  }

  if (trimmedCode.length !== 6) {
    return { isValid: false, error: 'Organization code must be exactly 6 characters' };
  }

  // Check for valid characters (letters and numbers only)
  const codeRegex = /^[A-Z0-9]{6}$/;
  if (!codeRegex.test(trimmedCode)) {
    return { isValid: false, error: 'Organization code must contain only letters and numbers' };
  }

  return { isValid: true, error: '' };
};

/**
 * File validation for uploads
 * @param {File} file - File to validate
 * @param {object} options - Validation options
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateFile = (file, options = {}) => {
  const {
    allowedTypes = ['image/*', 'application/pdf'],

  } = options;

  if (!file) {
    return { isValid: false, error: 'File is required' };
  }

  if (!(file instanceof File)) {
    return { isValid: false, error: 'Invalid file object' };
  }

  // Check file type
  const isValidType = allowedTypes.some(type => {
    if (type.endsWith('/*')) {
      const baseType = type.replace('/*', '');
      return file.type.startsWith(baseType);
    }
    return file.type === type;
  });

  if (!isValidType) {
    return { isValid: false, error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}` };
  }

  return { isValid: true, error: '' };
};

/**
 * Multiple files validation
 * @param {File[]} files - Array of files to validate
 * @param {object} options - Validation options
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateFiles = (files, options = {}) => {
  const { maxFiles = 10 } = options;

  if (!Array.isArray(files)) {
    return { isValid: false, error: 'Invalid files array' };
  }

  if (files.length === 0) {
    return { isValid: false, error: 'At least one file is required' };
  }

  if (files.length > maxFiles) {
    return { isValid: false, error: `Maximum ${maxFiles} files allowed` };
  }

  // Validate each file
  for (let i = 0; i < files.length; i++) {
    const fileValidation = validateFile(files[i], options);
    if (!fileValidation.isValid) {
      return { isValid: false, error: `File ${i + 1}: ${fileValidation.error}` };
    }
  }

  return { isValid: true, error: '' };
};

/**
 * Text content validation (for order details, notes, etc.)
 * @param {string} text - Text to validate
 * @param {string} fieldName - Name of the field for error messages
 * @param {object} options - Validation options
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateText = (text, fieldName = 'Text', options = {}) => {
  const {
    required = true,
    minLength = 1,
    maxLength = 1000,
    allowEmpty = false
  } = options;

  if (!text || typeof text !== 'string') {
    if (required && !allowEmpty) {
      return { isValid: false, error: `${fieldName} is required` };
    }
    return { isValid: true, error: '' };
  }

  const trimmedText = text.trim();
  
  if (required && trimmedText.length === 0 && !allowEmpty) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (trimmedText.length < minLength) {
    return { isValid: false, error: `${fieldName} must be at least ${minLength} characters long` };
  }

  if (trimmedText.length > maxLength) {
    return { isValid: false, error: `${fieldName} is too long (maximum ${maxLength} characters)` };
  }

  return { isValid: true, error: '' };
};

/**
 * Phone number validation (basic)
 * @param {string} phone - Phone number to validate
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validatePhone = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return { isValid: false, error: 'Phone number is required' };
  }

  const cleanedPhone = phone.replace(/\D/g, '');
  
  if (cleanedPhone.length < 10) {
    return { isValid: false, error: 'Phone number must be at least 10 digits' };
  }

  if (cleanedPhone.length > 15) {
    return { isValid: false, error: 'Phone number is too long' };
  }

  return { isValid: true, error: '' };
};

/**
 * Sanitize input to prevent XSS and injection attacks
 * @param {string} input - Input to sanitize
 * @returns {string} - Sanitized input
 */
export const sanitizeInput = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove potentially dangerous characters and patterns
  return input
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
};

/**
 * Validate form data object
 * @param {object} formData - Form data to validate
 * @param {object} validationRules - Validation rules for each field
 * @returns {object} - { isValid: boolean, errors: object }
 */
export const validateForm = (formData, validationRules) => {
  const errors = {};
  let isValid = true;

  for (const [fieldName, rule] of Object.entries(validationRules)) {
    const value = formData[fieldName];
    let fieldValidation;

    switch (rule.type) {
      case 'email':
        fieldValidation = validateEmail(value);
        break;
      case 'password':
        fieldValidation = validatePassword(value);
        break;
      case 'name':
        fieldValidation = validateName(value, rule.fieldName, rule.options);
        break;
      case 'organizationCode':
        fieldValidation = validateOrganizationCode(value);
        break;
      case 'text':
        fieldValidation = validateText(value, rule.fieldName, rule.options);
        break;
      case 'phone':
        fieldValidation = validatePhone(value);
        break;
      case 'file':
        fieldValidation = validateFile(value, rule.options);
        break;
      case 'files':
        fieldValidation = validateFiles(value, rule.options);
        break;
      default:
        fieldValidation = { isValid: true, error: '' };
    }

    if (!fieldValidation.isValid) {
      errors[fieldName] = fieldValidation.error;
      isValid = false;
    }
  }

  return { isValid, errors };
}; 

// Role validation constants
export const VALID_ROLES = {
  BUSINESS_OWNER: 'BusinessOwner',
  INVENTORY_MANAGER: 'InventoryManager',
  ACCOUNTANT: 'Accountant',
  SALESPERSON: 'Salesperson',
  ADMIN: 'Admin'
};

export const ROLE_PERMISSIONS = {
  [VALID_ROLES.BUSINESS_OWNER]: {
    canUploadAll: true,
    canManageUsers: true,
    canApproveOrders: true,
    canDeleteOrders: true,
    canManageInventory: true,
    canManageOrders: true
  },
  [VALID_ROLES.INVENTORY_MANAGER]: {
    canUploadAll: false,
    allowedFileTypes: ['challan', 'bilty', 'product'],
    canManageInventory: true,
    canDispatchOrders: true,
    canReadyToDispatch: true
  },
  [VALID_ROLES.ACCOUNTANT]: {
    canUploadAll: false,
    allowedFileTypes: ['bill', 'challan', 'bilty'],
    canViewAllOrders: true,
    canProcessBills: true
  },
  [VALID_ROLES.SALESPERSON]: {
    canUploadAll: false,
    allowedFileTypes: ['order', 'invoice'],
    canCreateOrders: true,
    canEditOrders: true,
    canViewAllFiles: true
  },
  [VALID_ROLES.ADMIN]: {
    canUploadAll: true,
    canManageUsers: true,
    canManageAll: true
  }
};

// Role validation functions
export const validateRole = (role) => {
  if (!role || typeof role !== 'string') {
    return { isValid: false, error: 'Role must be a non-empty string' };
  }
  
  if (!Object.values(VALID_ROLES).includes(role)) {
    return { 
      isValid: false, 
      error: `Invalid role. Must be one of: ${Object.values(VALID_ROLES).join(', ')}` 
    };
  }
  
  return { isValid: true, error: null };
};

export const canUploadFileType = (userRole, fileType) => {
  if (!userRole || !fileType) return false;
  
  const rolePermissions = ROLE_PERMISSIONS[userRole];
  if (!rolePermissions) return false;
  
  if (rolePermissions.canUploadAll) return true;
  
  return rolePermissions.allowedFileTypes && 
         rolePermissions.allowedFileTypes.includes(fileType);
};

export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;
  
  const rolePermissions = ROLE_PERMISSIONS[userRole];
  if (!rolePermissions) return false;
  
  return rolePermissions[permission] === true;
}; 